import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Admin → Jobs: the person pills on the Active / Closed Jobs toolbar (report a33884ec). Each pill lists the jobs with
// one person among their Resources, after All, in this list's order; who sees it and whether that person's jobs count
// in All are set here too. People are picked from the staff logins, so no name is written into the code — the server
// keys each pill by its user's login (jobResPills in routes/jobs.ts). The list is saved whole with Save. Until it has
// been saved once it shows the pills the toolbar has had all along, which are what the server goes by meanwhile, so an
// admin only adds what is new and saves.

type Pill = { userId: string; label: string; visibleTo: string[] | null; inAll: boolean };
type Staff = { id: string; email: string; name: string; nickname: string; role: string; active: boolean };

const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
});
const field: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 4px' };

export default function JobResPills() {
  const { t } = useTranslation();
  const [pills, setPills] = useState<Pill[] | null>(null);
  const [saved, setSaved] = useState(true);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const take = (d: any) => {
    setPills((d?.pills || []).map((p: any) => ({
      userId: String(p.userId || ''), label: String(p.label || ''),
      visibleTo: Array.isArray(p.visibleTo) ? p.visibleTo.map(String) : null, inAll: p.inAll !== false,
    })));
    setSaved(!!d?.saved);
  };
  useEffect(() => {
    Promise.all([api.adminGetJobResPills(), api.adminGetUsers()])
      .then(([d, u]: any[]) => {
        take(d);
        // Staff logins only: a client-portal or external login is never a Resource.
        setStaff((Array.isArray(u) ? u : []).filter((x: any) => x.role !== 'client' && x.role !== 'external'));
      })
      .catch((e: any) => setErr(e.message));
  }, []);

  const userOf = (id: string) => staff.find(u => u.id === id);
  const nameOf = (id: string) => { const u = userOf(id); return u ? (u.nickname || u.name || u.email) : ''; };
  const edit = (next: (ps: Pill[]) => Pill[]) => { setDone(false); setErr(''); setPills(ps => next(ps || [])); };
  const set = (i: number, patch: Partial<Pill>) => edit(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const move = (i: number, by: number) => edit(ps => { const a = [...ps]; const [x] = a.splice(i, 1); a.splice(i + by, 0, x); return a; });
  const toggleViewer = (i: number, id: string) => {
    const v = (pills && pills[i].visibleTo) || [];
    set(i, { visibleTo: v.includes(id) ? v.filter(x => x !== id) : [...v, id] });
  };

  // The server refuses the same lists (saveJobResPills); asked here first, as a refused call pages the admins.
  // One pill per person: two logins with the same name before the @ are one person, as the server reads them.
  const problem = (): string => {
    const seen = new Set<string>();
    for (const p of pills || []) {
      const u = userOf(p.userId);
      if (!u) return t('jrp_need_person');
      const local = String(u.email || '').trim().toLowerCase().split('@')[0];
      if (seen.has(local)) return t('jrp_twice', { name: nameOf(p.userId) });
      seen.add(local);
      if (p.visibleTo && !p.visibleTo.length) return t('jrp_need_viewer', { name: nameOf(p.userId) });
    }
    return '';
  };
  const save = async () => {
    const why = problem();
    if (why) { setErr(why); return; }
    setBusy(true); setErr('');
    try { take(await api.adminSetJobResPills(pills || [])); setDone(true); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="ti ti-users" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('jrp_title')}</div>
          <button onClick={() => edit(ps => [...ps, { userId: '', label: '', visibleTo: null, inAll: true }])} disabled={pills === null}
            style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), whiteSpace: 'nowrap' }}>
            <i className="ti ti-plus" aria-hidden /> {t('jrp_add')}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('jrp_desc')}</div>
        {!saved && (
          <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px', marginBottom: 12 }}>
            {t('jrp_unsaved')}
          </div>
        )}

        {pills === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : pills.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '10px 0', borderTop: '1px solid var(--border)' }}>{t('jrp_none')}</div>
        ) : pills.map((p, i) => (
          <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <select value={p.userId} onChange={e => set(i, { userId: e.target.value })} style={{ ...field, minWidth: 220 }}>
                <option value="">{t('jrp_pick_person')}</option>
                {staff.filter(u => u.active || u.id === p.userId).map(u => (
                  <option key={u.id} value={u.id}>{(u.nickname || u.name || u.email) + ' · ' + u.email}</option>
                ))}
              </select>
              <input value={p.label} onChange={e => set(i, { label: e.target.value })} maxLength={40}
                placeholder={nameOf(p.userId) || t('jrp_label')} title={t('jrp_label_hint')} style={{ ...field, width: 120 }} />
              <label title={t('jrp_in_all_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={p.inAll} onChange={e => set(i, { inAll: e.target.checked })} /> {t('jrp_in_all')}
              </label>
              <select value={p.visibleTo ? 'some' : 'all'} onChange={e => set(i, { visibleTo: e.target.value === 'all' ? null : (p.visibleTo || []) })} style={field}>
                <option value="all">{t('jrp_everyone')}</option>
                <option value="some">{t('jrp_only')}</option>
              </select>
              <div style={{ flex: 1 }} />
              <button onClick={() => move(i, -1)} disabled={i === 0} title={t('jrp_up')} style={{ ...iconBtn, opacity: i === 0 ? 0.35 : 1 }}><i className="ti ti-arrow-up" aria-hidden /></button>
              <button onClick={() => move(i, 1)} disabled={i === pills.length - 1} title={t('jrp_down')} style={{ ...iconBtn, opacity: i === pills.length - 1 ? 0.35 : 1 }}><i className="ti ti-arrow-down" aria-hidden /></button>
              <button onClick={() => edit(ps => ps.filter((_, j) => j !== i))} title={t('delete')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
            </div>
            {p.visibleTo && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {staff.filter(u => u.active || p.visibleTo!.includes(u.id)).map(u => {
                  const on = p.visibleTo!.includes(u.id);
                  return (
                    <button key={u.id} onClick={() => toggleViewer(i, u.id)}
                      style={{ padding: '5px 9px', borderRadius: 8, border: '1px solid', borderColor: on ? '#1E9E63' : 'var(--border-strong)', background: on ? '#F4FBF7' : 'transparent', color: on ? '#15803D' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <i className={`ti ${on ? 'ti-check' : 'ti-plus'}`} style={{ fontSize: 12 }} aria-hidden />{u.nickname || u.name || u.email}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          {done && <span style={{ fontSize: 12, color: '#15803D' }}>{t('jrp_saved')}</span>}
          <button onClick={save} disabled={busy || pills === null} style={{ ...btn('var(--accent)'), opacity: busy || pills === null ? 0.6 : 1 }}>
            {busy ? t('login_wait') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
